import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  LocationOn as MapPinIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  Schedule as ClockIcon,
  Send as SendIcon,
  Chat as MessageCircleIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import { API_BASE_URL } from '../services/api';

const Contact = () => {
  const locationState = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    location: 'north-oxford'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const propertyInquiry = locationState.state?.propertyInquiry;
    if (!propertyInquiry) return;

    const propertyTitle = propertyInquiry.title || 'Property Listing';
    const propertyCode = propertyInquiry.listingCode ? ` (${propertyInquiry.listingCode})` : '';
    const propertyType = propertyInquiry.propertyType || 'Property';
    const propertyPurpose = propertyInquiry.purpose || 'Inquiry';
    const propertyLocation = propertyInquiry.location || 'Selected Location';
    const propertyPrice = propertyInquiry.price ? `Price: £${Number(propertyInquiry.price).toLocaleString('en-GB')}` : '';

    setFormData((prev) => ({
      ...prev,
      subject: prev.subject || `${propertyPurpose} Inquiry - ${propertyTitle}${propertyCode}`,
      message: prev.message || `Hello, I am interested in this ${propertyType.toLowerCase()} listing:\n\n${propertyTitle}${propertyCode}\nLocation: ${propertyLocation}${propertyPrice ? `\n${propertyPrice}` : ''}\n\nPlease share more details about availability and next steps.`
    }));
  }, [locationState.state]);

  const locations = [
    {
      id: 'north-oxford',
      name: 'Central North Oxford Office',
      address: '113 Walton Street, Oxford, OX2 6AJ',
      phone: '01865 554422',
      email: 'northoxford@jcpestateagents.co.uk',
      hours: 'Sales and Lettings Support Available',
      mapEmbed: 'https://www.google.com/maps?q=113+Walton+Street,+Oxford,+OX2+6AJ&output=embed',
      salesEmail: 'northoxford@jcpestateagents.co.uk',
      salesPhone: '01865 554422',
      lettingsEmail: 'lettings@jcpestateagents.co.uk',
      lettingsPhone: '01865 554422'
    },
    {
      id: 'east-oxford',
      name: 'East Oxford Office',
      address: '251 Cowley Road, Oxford, OX4 1XG',
      phone: '01865721122',
      email: 'eastoxford@jcpestateagents.co.uk',
      hours: 'Sales and Lettings Support Available',
      mapEmbed: 'https://www.google.com/maps?q=251+Cowley+Road,+Oxford,+OX4+1XG&output=embed',
      salesEmail: 'eastoxford@jcpestateagents.co.uk',
      salesPhone: '01865721122',
      lettingsEmail: 'lettings@jcpestateagents.co.uk',
      lettingsPhone: '01865721122'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to API
      await axios.post(`${API_BASE_URL}/ContactMessage`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        location: formData.location
      });

      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        location: 'north-oxford'
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      alert('Failed to send message. Please try again.');
    }
  };

  const selectedLocation = locations.find(loc => loc.id === formData.location);

  return (
    <Box sx={{ py: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Seo
        title="Contact Us"
        description="Contact JCP Estate Agents for sales, lettings, customer care, and local property guidance across Oxford and Oxfordshire."
        keywords="JCP Estate Agents contact, Oxford estate agents, lettings contact, sales contact, customer care"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2c5aa0 0%, #1e3a6f 50%, #0f3460 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.2) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Playfair Display',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                background: 'linear-gradient(45deg, #ffffff 30%, #d4af37 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h5"
              sx={{
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Speak with our sales and lettings teams for support, valuations, property advice, and customer care assistance.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Contact Form - Left Side */}
          <Grid item xs={12} md={7.5} lg={7.8}>
            <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <MessageCircleIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold">
                  Send us a Message
                </Typography>
              </Box>

              {submitted ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      backgroundColor: 'success.light',
                      borderRadius: '50%',
                      mb: 3,
                    }}
                  >
                    <SendIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Message Sent!
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Thank you for contacting JCP Estate Agents. We'll get back to you shortly.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setSubmitted(false)}
                    size="large"
                  >
                    Send Another Message
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Row 1: Name and Email */}
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        size="large"
                      />
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        size="large"
                      />
                    </Box>
                    
                    {/* Row 2: Phone and Subject */}
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        variant="outlined"
                        size="large"
                      />
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        size="large"
                      />
                    </Box>
                    
                    {/* Row 3: Message - Full Width */}
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      placeholder="Please provide details about your inquiry..."
                    />
                    
                    {/* Row 4: Submit Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={<SendIcon />}
                        disabled={isSubmitting}
                        sx={{ 
                          px: 6, 
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          minWidth: 200,
                        }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Location Information - Right Side */}
          <Grid item xs={12} md={4.5} lg={4.2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth size="large">
                <InputLabel sx={{ fontSize: '1.1rem', fontWeight: 500 }}>Select Location</InputLabel>
                <Select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  label="Select Location"
                  sx={{
                    fontSize: '1.1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id} sx={{ fontSize: '1.1rem' }}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Current Location Info */}
              <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 2 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  {selectedLocation.name}
                </Typography>
                <List>
                  <ListItem disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <MapPinIcon color="primary" fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={selectedLocation.address}
                      primaryTypographyProps={{ variant: 'body1', fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneIcon color="primary" fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={selectedLocation.phone}
                      primaryTypographyProps={{ variant: 'body1', fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <MailIcon color="primary" fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={selectedLocation.email}
                      primaryTypographyProps={{ variant: 'body1', fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ClockIcon color="primary" fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={selectedLocation.hours}
                      primaryTypographyProps={{ variant: 'body1', fontSize: '1rem' }}
                    />
                  </ListItem>
                </List>

                <Stack spacing={2.2} sx={{ mt: 3 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Sales</Typography>
                    <Typography variant="body2">E: {selectedLocation.salesEmail}</Typography>
                    <Typography variant="body2">T: {selectedLocation.salesPhone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Lettings</Typography>
                    <Typography variant="body2">E: {selectedLocation.lettingsEmail}</Typography>
                    <Typography variant="body2">T: {selectedLocation.lettingsPhone}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                    Please be aware: Calls may be recorded or monitored for training, quality assurance, and record-keeping purposes.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Contact Sales | Contact Lettings
                  </Typography>
                </Stack>
              </Paper>

              <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Customer Care
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
                  JCP Estate Agents strive to ensure that the process of buying, selling and letting is as smooth as possible. However, if you have any comments regarding our service, please contact us on customerservice@jcpestateagents.co.uk
                </Typography>
              </Paper>

              {/* All Locations Quick Access */}
              <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Quick Access
                </Typography>
                <Grid container spacing={2}>
                  {locations.map((location) => (
                    <Grid item xs={12} key={location.id}>
                      <Button
                        fullWidth
                        variant={formData.location === location.id ? 'contained' : 'outlined'}
                        size="large"
                        onClick={() => setFormData(prev => ({ ...prev, location: location.id }))}
                        sx={{ 
                          justifyContent: 'flex-start', 
                          textTransform: 'none',
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 500,
                          borderRadius: 2,
                          '&:hover': {
                            transform: 'translateX(4px)',
                            boxShadow: 2,
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {location.name}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        {/* Full Width Map Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" sx={{ mb: 4 }}>
            Find Us on Map
          </Typography>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box
              component="iframe"
              src={selectedLocation?.mapEmbed}
              sx={{
                width: '100%',
                height: 450,
                border: 0,
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;
